import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { money } from '@/lib/format';
import { ErrorNote } from '@/components/Layout';

/** How a payment arrived, in words, for tables and CSVs. */
export function receivedBy(p: { provider: string; payment_method_id?: string | null; collected_by?: { first_name: string; last_name: string } | null }): string {
  switch (p.provider) {
    case 'gocardless': return p.payment_method_id ? 'Direct Debit' : 'Pay by Bank';
    case 'paypal': return 'PayPal';
    case 'cash': return 'Cash';
    case 'bank_transfer': return 'Bank transfer';
    case 'instructor': return p.collected_by ? `Collected by ${p.collected_by.first_name} ${p.collected_by.last_name}` : 'Collected by instructor';
    case 'waived': return 'Waived';
    default: return p.provider;
  }
}

export interface SettleValues { provider: 'cash' | 'bank_transfer' | 'instructor' | 'waived'; received_on: string; reference: string; note: string; collected_by: string; amount: string; discount: string; discount_reason: string }

const today = () => new Date().toISOString().slice(0, 10);
/** Pounds typed by an admin to whole pence (blank is 0). */
export const toPence = (pounds: string) => Math.round(Number(pounds || 0) * 100);

/** Active instructors, for "handed over by". */
function useInstructors() {
  return useQuery({
    queryKey: ['settle-instructors'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from('dojo_instructor').select('person:person_id(id,first_name,last_name),dojo:dojo_id(short_name,name)').eq('active', true);
      if (error) throw error;
      const rows = data as unknown as { person: { id: string; first_name: string; last_name: string } | null; dojo: { short_name: string | null; name: string } | null }[];
      const byPerson = new Map<string, { id: string; label: string }>();
      for (const r of rows) if (r.person) {
        const dojo = r.dojo?.short_name ?? r.dojo?.name ?? '';
        const prev = byPerson.get(r.person.id);
        byPerson.set(r.person.id, { id: r.person.id, label: prev ? `${prev.label}, ${dojo}` : `${r.person.first_name} ${r.person.last_name} (${dojo}` });
      }
      return [...byPerson.values()].map(x => ({ ...x, label: `${x.label})` })).sort((a, b) => a.label.localeCompare(b.label));
    },
  });
}

const ERRORS: Record<string, string> = {
  already_paid: 'One of these is already paid for. Refresh and choose again.',
  not_an_instructor: 'Choose the instructor who collected the payment.',
  reason_required: 'Give a reason for waiving the charge.',
  bad_date: 'The date received cannot be in the future.',
  withdrawn: 'A withdrawn entry cannot be paid for.',
  not_admin: 'Only admins can record payments.',
  discount_reason_required: 'Give a reason for the discount.',
  over_owed: 'That is more than the amount outstanding.',
  one_household_for_part_payment: 'A part payment or discount can only cover one household. Choose one household\'s entries.',
  nothing_outstanding: 'There is no outstanding balance on this payment.',
  total_mismatch: 'The amount received does not match the prices less the discount.',
  overlaps: 'Someone already has a membership covering those dates.',
  frozen: 'Someone chosen has a paused membership. Unfreeze them first.',
  part_of_direct_debit: 'The rest of this household renews by Direct Debit on the same date. Include everyone on the Direct Debit, or cancel it first.',
  unknown_plan: 'Choose a plan for everyone.',
};
export const settleError = (message: string) => ERRORS[Object.keys(ERRORS).find(k => message.includes(k)) ?? ''] ?? `Could not save: ${message}`;

/**
 * Record money received outside the portal (cash, bank transfer, or handed over by an instructor), or free of charge.
 * `owedPence` is what the chosen bookings or fees add up to; null when the amount is entered per line.
 */
export function SettleForm({ count, owedPence, onSave, onCancel, part = false, title }: { count: number; owedPence: number | null; onSave: (v: SettleValues) => Promise<string | null>; onCancel: () => void; part?: boolean | 'discount' | 'amount'; title?: string }) {
  const instructors = useInstructors();
  const [v, setV] = useState<SettleValues>({ provider: 'cash', received_on: today(), reference: '', note: '', collected_by: '', amount: '', discount: '', discount_reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (p: Partial<SettleValues>) => setV({ ...v, ...p });
  const foc = v.provider === 'waived';
  async function save() {
    if (v.provider === 'instructor' && !v.collected_by) { setError('Choose the instructor who handed the money over.'); return; }
    if (foc && !v.note.trim()) { setError('Give a reason for waiving the charge.'); return; }
    if (!foc && Number(v.discount || 0) > 0 && !v.discount_reason.trim()) { setError('Give a reason for the discount.'); return; }
    setBusy(true); setError('');
    const e = await onSave(v);
    setBusy(false);
    if (e) setError(e);
  }
  return (
    <div className="space-y-3 rounded-sm bg-stone-50 p-4">
      <p className="font-semibold">{title ?? <>{foc ? 'Waive' : 'Record payment'}: {count} {count === 1 ? 'item' : 'items'}</>}{!foc && owedPence != null && <span className="font-normal text-ink-500"> · {money(owedPence)}</span>}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block"><span className="label">Payment method</span>
          <select className="input" value={v.provider} onChange={e => set({ provider: e.target.value as SettleValues['provider'] })}>
            <option value="cash">Cash</option><option value="bank_transfer">Bank transfer</option><option value="instructor">Collected by instructor</option><option value="waived">Waived (no charge)</option>
          </select></label>
        {v.provider === 'instructor' && <label className="block"><span className="label">Instructor</span>
          <select className="input" value={v.collected_by} onChange={e => set({ collected_by: e.target.value })}>
            <option value="">Choose…</option>{(instructors.data ?? []).map(i => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select></label>}
        {!foc && <label className="block"><span className="label">Date received</span><input type="date" className="input" max={today()} value={v.received_on} onChange={e => set({ received_on: e.target.value })} /></label>}
        {!foc && <label className="block"><span className="label">Reference (optional)</span><input className="input" value={v.reference} onChange={e => set({ reference: e.target.value })} placeholder="e.g. bank ref or envelope no." /></label>}
        {(part === true || part === 'discount') && !foc && <label className="block"><span className="label">Discount £ (optional)</span><input className="input" inputMode="decimal" value={v.discount} onChange={e => set({ discount: e.target.value.replace(/[^\d.]/g, '') })} /></label>}
        {(part === true || part === 'discount') && !foc && Number(v.discount || 0) > 0 && <label className="block"><span className="label">Discount reason</span><input className="input" value={v.discount_reason} onChange={e => set({ discount_reason: e.target.value })} placeholder="e.g. sibling" /></label>}
        {(part === true || part === 'amount') && !foc && owedPence != null && <label className="block"><span className="label">Amount received £</span><input className="input" inputMode="decimal" placeholder={((owedPence - toPence(v.discount)) / 100).toFixed(2)} value={v.amount} onChange={e => set({ amount: e.target.value.replace(/[^\d.]/g, '') })} /><span className="mt-1 block text-xs text-ink-500">Leave blank to settle in full. A smaller amount leaves the remainder outstanding.</span></label>}
        <label className={`block ${foc ? 'sm:col-span-2 lg:col-span-3' : 'sm:col-span-2 lg:col-span-4'}`}><span className="label">{foc ? 'Reason' : 'Note (optional)'}</span><input className="input" value={v.note} onChange={e => set({ note: e.target.value })} placeholder={foc ? 'e.g. helping on the desk' : ''} /></label>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : foc ? 'Waive' : 'Record payment'}</button>
        <button className="btn-ghost border border-stone-300" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** The arguments the settle functions take. */
export const settleArgs = (v: SettleValues) => ({
  p_provider: v.provider, p_received_on: v.received_on || today(), p_reference: v.reference.trim() || null,
  p_note: v.note.trim() || null, p_collected_by: v.provider === 'instructor' ? v.collected_by : null,
});

/** Settling event bookings, which can be part paid and discounted. */
export const settlePartArgs = (v: SettleValues) => ({
  ...settleArgs(v),
  p_amount_pence: v.provider === 'waived' || !v.amount ? null : toPence(v.amount),
  p_discount_pence: v.provider === 'waived' ? 0 : toPence(v.discount),
  p_discount_reason: v.provider === 'waived' || !toPence(v.discount) ? null : v.discount_reason.trim(),
});

/** Grading fees: discount only (each fee is typed per candidate). */
export const settleDiscountArgs = (v: SettleValues) => ({
  ...settleArgs(v),
  p_discount_pence: v.provider === 'waived' ? 0 : toPence(v.discount),
  p_discount_reason: v.provider === 'waived' || !toPence(v.discount) ? null : v.discount_reason.trim(),
});

/** Status pills for payments and refunds, shared by the admin pages. */
export const PAYMENT_PILL: Record<string, string> = {
  confirmed: 'bg-emerald-100 text-emerald-800', paid_out: 'bg-emerald-100 text-emerald-800', paid: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-sky-100 text-sky-800', failed: 'bg-brand-100 text-brand-800', refunded: 'bg-stone-100 text-ink-700', cancelled: 'bg-stone-100 text-ink-700',
};
